import classNames from "classnames";
import { InputHTMLAttributes, ForwardedRef, forwardRef, useId } from "react";
import { Icon, IconNames } from "./Icon";
import { FormErrorOrWarning } from "./FormErrorOrWarning";

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hideLabel?: boolean;
  helperText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  icon?: { position: "leading" | "trailing"; name: IconNames };
  error?: { type: "error" | "warning"; message?: string };
  className?: string;
};

export const inputClasses =
  "rounded-md px-2 h-10 outline-none w-full border border-gray-7 text-gray-1 font-style-body-md placeholder:text-gray-3 placeholder:font-style-body-md read-only:text-gray-5 disabled:text-gray-5 read-only:cursor-not-allowed disabled:cursor-not-allowed disabled:border-gray-6 read-only:border-gray-6 disabled:bg-transparent [&:not(:disabled,_:read-only)]:active:border-gray-3 [&:not(:disabled,_:read-only)]:hover:border-gray-3 [&:not(:disabled,_:read-only)]:focus:border-gray-3";

export const labelClasses = "block mb-1 text-gray-1 font-style-label-sm";

export const helperTextClasses = "mt-1 text-gray-3 font-style-body-xs";

export const Input = forwardRef(function Input(
  {
    label,
    hideLabel = false,
    helperText,
    disabled,
    readOnly,
    icon,
    error,
    className,
    ...props
  }: InputProps,
  ref: ForwardedRef<HTMLInputElement>
) {
  const inputRandomId = useId();
  const helperTextRandomId = useId();
  const errorRandomId = useId();

  const getDescriptionIds = () => {
    if (helperText && error?.message) {
      return `${helperTextRandomId} ${errorRandomId}`;
    } else if (helperText || error?.message) {
      return helperText ? helperTextRandomId : errorRandomId;
    }
  };

  return (
    <>
      <label
        data-testid="input-label"
        htmlFor={inputRandomId}
        className={classNames(hideLabel ? "sr-only" : labelClasses, {
          ["text-gray-2"]: disabled || readOnly,
        })}
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={inputRandomId}
          className={classNames(
            inputClasses,
            {
              ["pl-8"]: icon?.position === "leading",
              ["pr-8"]: icon?.position === "trailing",
              ["!border-danger-2"]: error?.type === "error" && error?.message,
              ["!border-warning-2"]:
                error?.type === "warning" && error?.message,
              ["bg-danger-3"]: error?.message,
            },
            className
          )}
          ref={ref}
          aria-describedby={getDescriptionIds()}
          disabled={disabled}
          readOnly={readOnly}
          {...props}
        />
        {icon?.name && (
          <span
            data-testid="icon-wrapper"
            className={classNames(
              "absolute bottom-1/2 translate-y-1/2 inline-flex",
              {
                ["left-1.5"]: icon?.position === "leading",
                ["right-1.5"]: icon?.position === "trailing",
                ["text-gray-2"]: disabled || readOnly,
              }
            )}
          >
            <Icon name={icon?.name} />
          </span>
        )}
      </div>
      {helperText && (
        <div
          id={helperTextRandomId}
          className={classNames(helperTextClasses, {
            ["text-gray-2"]: disabled || readOnly,
          })}
        >
          {helperText}
        </div>
      )}
      <FormErrorOrWarning
        id={errorRandomId}
        type={error?.type}
        isVisible={!!error?.message}
        message={error?.message}
      />
    </>
  );
});
